import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilmCard from "@/components/feature/FilmCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { getFilmById, getRelated } from "@/lib/filmData";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { TYPE_LABEL } from "@/types/film";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const film = id ? getFilmById(id) : undefined;
  const [toast, setToast] = useState<{ ok: boolean; message: string } | null>(null);

  useDocumentMeta({
    title: film ? `${film.title} - 新金瓶梅影视详情` : "影视内容不存在 - 新金瓶梅",
    description: film
      ? `${film.title}剧情简介：${film.summary}`
      : "抱歉，未找到对应的影视内容，请返回首页浏览其他影视作品。",
    keywords: film
      ? `${film.title},${film.genres.join(",")},${film.cast.slice(0, 2).join(",")},新金瓶梅,剧情介绍`
      : "新金瓶梅,影视详情",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!film) {
    return (
      <div className="page-shell flex min-h-[60vh] flex-col items-center justify-center pt-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
          <i className="ri-error-warning-line" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-extrabold text-foreground-950">
          影视内容不存在
        </h1>
        <p className="mt-3 max-w-md text-sm text-foreground-500">
          你访问的影视作品可能已下架或链接有误，返回首页可以继续浏览其它精彩内容。
        </p>
        <Link
          to="/"
          className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line" aria-hidden="true"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(film, 4);
  const categoryLabel = TYPE_LABEL[film.type];

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("textarea");
        input.value = url;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setToast({ ok: true, message: "页面链接已复制到剪贴板" });
    } catch {
      setToast({ ok: false, message: `复制失败，请手动复制：${url}` });
    }
  };

  const infoRows = [
    { label: "作品类型", value: categoryLabel },
    { label: "上映年份", value: `${film.year} 年` },
    { label: "制片地区", value: film.region },
    { label: "作品评分", value: `${film.rating.toFixed(1)} 分` },
    { label: "导\u3000\u3000演", value: film.director },
    { label: "主\u3000\u3000演", value: film.cast.join(" / ") },
  ];

  return (
    <div className="page-shell pb-8 pt-24 md:pt-28">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: `${categoryLabel}频道`, to: `/category/${film.type}` },
          { label: film.title },
        ]}
      />

      <article className="mt-2">
        <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-[300px_1fr]">
          <div className="mx-auto w-[220px] sm:w-[260px] lg:mx-0 lg:w-full">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <div className="aspect-[2/3] w-full">
                <img
                  src={film.cover}
                  alt={`${film.title} 高清海报 - 新金瓶梅影视详情`}
                  title={`${film.title} - 新金瓶梅影视详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleShare}
              className="mt-3 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-share-forward-line" aria-hidden="true"></i>
              分享此页面
            </button>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${film.type}`}
                className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-200"
              >
                {categoryLabel}
              </Link>
              <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">
                <i className="ri-star-fill" aria-hidden="true"></i>
                {film.rating.toFixed(1)} 分
              </span>
              <span className="text-xs text-foreground-400">热度 {film.popularity}</span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
              {film.title}
            </h1>

            <div className="mt-3 flex flex-wrap gap-2">
              {film.genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
                >
                  {genre}
                </span>
              ))}
            </div>

            <p className="mt-5 rounded-lg border-l-4 border-accent-500 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {film.summary}
            </p>

            <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-3 border-b border-background-200 pb-3">
                  <dt className="w-20 shrink-0 text-sm text-foreground-500">{row.label}</dt>
                  <dd className="text-sm font-medium text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          <section>
            <h2 className="flex items-center gap-2.5">
              <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
              <span className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                剧情详情
              </span>
            </h2>
            <div className="mt-5 space-y-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              {film.plot.map((paragraph, index) => (
                <p key={index} className="text-sm leading-loose text-foreground-700 md:text-base">
                  {paragraph}
                </p>
              ))}
              <p className="rounded-md bg-background-100 p-3 text-xs text-foreground-500">
                以上剧情介绍为示例内容，仅用于展示本站的内容结构。
              </p>
            </div>

            <div className="mt-6">
              <Link
                to={`/category/${film.type}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" aria-hidden="true"></i>
                返回{categoryLabel}频道查看更多作品
              </Link>
            </div>
          </section>

          <aside>
            <SectionHeading title="相关推荐" subtitle="同类题材与相关作品" />
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-1">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                >
                  <div className="h-[90px] w-[64px] shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title} 封面`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-1 font-heading text-sm font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-[11px] text-foreground-400">
                      {item.year} · {item.region}
                    </p>
                    <p className="mt-1.5 line-clamp-2 text-xs text-foreground-500">{item.summary}</p>
                    <span className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-accent-600">
                      <i className="ri-star-fill" aria-hidden="true"></i>
                      {item.rating.toFixed(1)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </article>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div
            role="status"
            className={`animate-fade-up flex max-w-md items-center gap-2.5 rounded-md px-4 py-3 text-sm text-background-50 ${
              toast.ok ? "bg-foreground-900" : "bg-accent-700"
            }`}
          >
            <i className={toast.ok ? "ri-checkbox-circle-line text-lg" : "ri-alert-line text-lg"} aria-hidden="true"></i>
            <span className="break-all">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}